import styled, { css } from 'styled-components';
import { MenuItem } from 'react-rainbow-components';
import ButtonMenu from 'components/ButtonMenu';
import Drawer from 'components/Drawer';
import Card from 'components/Card';
import {
    GREEN_800,
    GREY_300,
    GREY_500,
    GREY_800,
    PURPLE_500,
    RED_100,
    RED_500,
    RED_700,
    WHITE,
} from '../../constants';

export const PageCard = styled(Card)`
    height: calc(100vh - 161px);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
`;

export const Container = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

export const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.25rem 1.5rem 0.75rem;
    flex-wrap: wrap;
`;

export const HeaderCopy = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

export const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

export const TableContainer = styled.div`
    display: flex;
    flex-direction: column;
    overflow: auto;
    height: 100%;
`;

export const PaginationContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding: 2rem 1rem 1rem 1.25rem;
    background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, ${WHITE} 40%);
    margin-top: -2rem;
    z-index: 1;
`;

export const EntriesText = styled.span`
    font-size: 0.8125rem;
    font-weight: 300;
    line-height: 1.25rem;
    color: ${GREY_300};
`;

export const PaginationButtons = styled.div`
    display: flex;
    gap: 0.25rem;
`;

export const CellWrapper = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0.75rem 1rem;
    overflow: hidden;
    color: ${GREY_500};
    font-size: 0.9375rem;
    font-weight: 300;
`;

export const UserCellWrapper = styled(CellWrapper)`
    padding: 0.625rem 0.625rem 0.625rem 1.25rem;
`;

export const InvoiceName = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    min-width: 0;
`;

export const InvoiceNumber = styled.button<{ $isVoid?: boolean }>`
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    font-weight: 500;
    text-align: left;
    color: ${PURPLE_500};
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }

    ${({ $isVoid }) => $isVoid && css`
        color: ${RED_500};
        text-decoration: line-through;

        &:hover {
            text-decoration: line-through underline;
        }
    `}
`;

export const SignatureIcon = styled.button<{ $signed?: boolean }>`
    display: inline-flex;
    flex-shrink: 0;
    padding: 0.125rem;
    border: none;
    border-radius: 0.25rem;
    background: none;
    cursor: pointer;
    color: ${({ $signed }) => ($signed ? GREEN_800 : GREY_300)};

    &:hover,
    &[aria-expanded='true'] {
        background: rgba(0, 0, 0, 0.04);
    }

    svg {
        width: 1rem;
        height: 1rem;
    }
`;

export const SignatureDropdown = styled.div`
    width: 20rem;
    padding: 1rem 1.25rem;
    border-radius: 1rem;
    background: ${WHITE};
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.06), 0 8px 24px 0 rgba(75, 70, 92, 0.16);
`;

export const SignatureStatus = styled.span<{ $signed?: boolean }>`
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1rem;
    color: ${({ $signed }) => ($signed ? GREEN_800 : GREY_500)};
`;

export const VoidText = styled.span`
    text-decoration: line-through;
`;

export const ActionsCell = styled(CellWrapper)`
    gap: 0.25rem;
    padding: 0.75rem 0.5rem;
    overflow: visible;
`;

export const VoidMenuItem = styled(MenuItem)`
    &&,
    &&:hover,
    &&:focus,
    &&:active {
        color: ${RED_500};
    }

    svg {
        color: ${RED_500};
    }
`;

export const InvoiceActionsMenu = styled(ButtonMenu)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 2rem;
    height: 2rem;

    && button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
    }
`;

export const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 0.5rem;
    padding: 4rem 1.5rem;
`;

export const EmptyTitle = styled.h2`
    margin: 0;
    font-size: 1.25rem;
    font-weight: 500;
    color: ${GREY_800};
`;

export const EmptyDescription = styled.p`
    margin: 0 0 0.75rem;
    font-size: 0.875rem;
    color: ${GREY_500};
    max-width: 28rem;
`;

export const ViewDrawer = styled(Drawer)<{ $wide?: boolean }>`
    width: min(${({ $wide }) => ($wide ? '62rem' : '46rem')}, calc(100vw - 3rem));

    & > div:first-child {
        border-bottom: none;
        padding-bottom: 0;
    }

    div[id^='drawer-content'] {
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        height: 100%;
        min-height: 0;
    }
`;

export const DrawerTitle = styled.span`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.2rem;
    min-width: 0;
    max-width: 100%;
`;

export const DrawerTitleMain = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
    max-width: 100%;
`;

export const DrawerTitleText = styled.span`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const VoidReasonBanner = styled.div`
    margin: 0;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    background: ${RED_100};
    color: ${RED_700};
`;

export const VoidReasonLabel = styled.div`
    margin-bottom: 0.25rem;
    color: ${RED_500};
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

export const VoidReasonText = styled.p`
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.4;
    white-space: pre-wrap;
`;
